import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { api, type ApiPhase } from '../../lib/api';
import { friendlyErrorMessage } from '../../lib/errorMessages';

interface Props {
  phase: ApiPhase;
  canEdit: boolean;
}

export function PhaseNotesEditor(props: Props) {
  // A different phase or lost permission must never inherit an open draft or
  // the completion callback of another phase's pending save.
  return <PhaseNotesForm key={`${props.phase.id}:${props.canEdit}`} {...props} />;
}

function PhaseNotesForm({ phase, canEdit }: Props) {
  const inputId = useId();
  const queryClient = useQueryClient();
  const [edit, setEdit] = useState<{ original: string; draft: string } | null>(null);
  const save = useMutation({
    mutationFn: ({ phaseId, notes }: { phaseId: string; notes: string | null }) =>
      api.phases.update(phaseId, { notes }),
    retry: false,
    onSuccess: async (saved) => {
      queryClient.setQueryData<ApiPhase[]>(['phases', phase.job_id], (current) =>
        current?.map((item) => (item.id === saved.id ? saved : item)),
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['phases', phase.job_id] }),
        queryClient.invalidateQueries({ queryKey: ['jobs'] }),
        queryClient.invalidateQueries({ queryKey: ['board'] }),
      ]);
      setEdit(null);
    },
  });

  return (
    <div className="pt-1">
      {canEdit && edit ? (
        <form
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (save.isPending || edit.draft.length > 2000) return;
            if (edit.draft === edit.original) {
              setEdit(null);
              return;
            }
            // Retain meaningful whitespace and line breaks; only blank notes
            // become null. The request carries no other phase fields.
            save.mutate({ phaseId: phase.id, notes: edit.draft.trim() ? edit.draft : null });
          }}
        >
          <label htmlFor={inputId} className="block font-semibold text-neutral-700">
            Phase notes
          </label>
          <textarea
            id={inputId}
            value={edit.draft}
            onChange={(event) => setEdit({ ...edit, draft: event.target.value })}
            maxLength={2000}
            rows={3}
            disabled={save.isPending}
            className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#F26522] disabled:opacity-60"
          />
          <p className="text-neutral-500">{edit.draft.length} / 2,000 characters</p>
          {save.isError && (
            <p role="alert" className="text-red-700">
              {friendlyErrorMessage(save.error)}
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={save.isPending || edit.draft.length > 2000}
              className="rounded-md bg-[#F26522] px-3 py-1.5 text-white disabled:opacity-50"
            >
              {save.isPending ? 'Saving notes…' : 'Save notes'}
            </button>
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => setEdit(null)}
              className="rounded-md px-3 py-1.5 text-neutral-600 hover:bg-neutral-100 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-neutral-700">Phase notes</span>
            {canEdit && (
              <button
                type="button"
                aria-label="Edit phase notes"
                onClick={() => {
                  save.reset();
                  setEdit({ original: phase.notes ?? '', draft: phase.notes ?? '' });
                }}
                className="rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-[#F26522]"
              >
                <Pencil className="h-3 w-3" />
              </button>
            )}
          </div>
          <p className="whitespace-pre-wrap text-neutral-600">{phase.notes || 'No phase notes'}</p>
        </>
      )}
    </div>
  );
}
